import React, { useState } from 'react';
import axios from 'axios';
import { getApiUrl } from '../utils/apiConfig';
import { 
  Button, 
  TextField, 
  Typography, 
  Box, 
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip
} from '@mui/material';

const CSVUpload = () => {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState('');
  const [uploadResult, setUploadResult] = useState(null);
  const [showDetailsDialog, setShowDetailsDialog] = useState(false);

  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setMessage('');
  };

  const handleUpload = async () => {
    if (!file) {
      setMessage('Bitte wählen Sie eine Datei aus');
      return;
    }

    const formData = new FormData();
    formData.append('file', file);

    setLoading(true);
    setMessage('');

    const uploadUrl = getApiUrl('/api/kontobewegungcsv/upload');
    console.log('📤 Starte CSV-Upload...');
    console.log('   📍 URL:', uploadUrl);
    console.log('   📁 Datei:', file.name, `(${(file.size / 1024).toFixed(2)} KB)`);
    console.log('   🌍 Environment:', process.env.NODE_ENV);

    try {
      const response = await axios.post(uploadUrl, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        withCredentials: true
      });

      console.log('✅ CSV-Upload erfolgreich:', response.data);
      const { message, inserted, duplicates, invalidRows } = response.data;
      
      // Upload-Ergebnis speichern
      setUploadResult({
        inserted: inserted || 0,
        duplicates: duplicates || 0,
        invalidRows: invalidRows || [],
        success: true
      });
      
      let statusMessage = message;
      if (invalidRows && invalidRows.length > 0) {
        statusMessage += ` (${invalidRows.length} fehlerhafte Zeilen)`;
      }
      
      setMessage(statusMessage);
      
      // Dialog automatisch öffnen wenn es Probleme gab
      if (invalidRows && invalidRows.length > 0) {
        setShowDetailsDialog(true);
      }
    } catch (error) {
      console.error('❌ Fehler beim CSV-Upload:', error);
      
      let errorMessage = 'Fehler beim Hochladen der Datei';
      
      if (error.response) {
        // Server hat mit Fehlercode geantwortet
        errorMessage = error.response.data.error || error.response.data.message || errorMessage;
        console.error('Server-Fehler:', error.response.status, error.response.data);
      } else if (error.request) {
        // Request wurde gesendet, aber keine Antwort erhalten
        errorMessage = 'Keine Antwort vom Server. Bitte überprüfen Sie Ihre Verbindung.';
        console.error('Keine Server-Antwort:', error.request);
      } else {
        // Fehler beim Erstellen des Requests
        errorMessage = error.message;
        console.error('Request-Fehler:', error.message);
      }
      
      setMessage(errorMessage);
    } finally {
      setLoading(false);
      setFile(null); // Reset file input
    }
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom>CSV-Upload für Kontobewegungen</Typography>
      
      <Box my={2}>
        <TextField
          type="file"
          onChange={handleFileChange}
          inputProps={{ accept: '.csv' }}
        />
      </Box>

      <Button
        variant="contained"
        color="primary"
        onClick={handleUpload}
        disabled={loading || !file}
      >
        {loading ? <CircularProgress size={24} /> : 'Upload'}
      </Button>

      {message && (
        <Box mt={2}>
          <Alert 
            severity={message.includes('Fehler') ? 'error' : 'success'}
            action={
              uploadResult && (
                <Button color="inherit" size="small" onClick={() => setShowDetailsDialog(true)}>
                  Details
                </Button>
              )
            }
          >
            {message}
          </Alert>
        </Box>
      )}

      {uploadResult && (
        <Box mt={2}>
          <Box display="flex" gap={2}>
            <Chip 
              label={`${uploadResult.inserted} eingefügt`} 
              color="success" 
              variant="outlined"
            />
            <Chip 
              label={`${uploadResult.duplicates} Duplikate`} 
              color="default" 
              variant="outlined"
            />
            {uploadResult.invalidRows.length > 0 && (
              <Chip 
                label={`${uploadResult.invalidRows.length} übersprungen`} 
                color="warning" 
                variant="outlined"
                onClick={() => setShowDetailsDialog(true)}
                sx={{ cursor: 'pointer' }}
              />
            )}
          </Box>
        </Box>
      )}

      {/* Details Dialog */}
      <Dialog 
        open={showDetailsDialog} 
        onClose={() => setShowDetailsDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Upload-Details
        </DialogTitle>
        <DialogContent>
          {uploadResult && (
            <>
              <Box mb={3}>
                <Typography variant="h6" gutterBottom>Zusammenfassung</Typography>
                <Box display="flex" gap={2} mb={2}>
                  <Chip label={`${uploadResult.inserted} erfolgreich eingefügt`} color="success" />
                  <Chip label={`${uploadResult.duplicates} Duplikate übersprungen`} color="default" />
                  <Chip label={`${uploadResult.invalidRows.length} fehlerhafte Zeilen`} color="warning" />
                </Box>
              </Box>

              {uploadResult.invalidRows.length > 0 && (
                <>
                  <Typography variant="h6" gutterBottom color="warning.main">
                    Übersprungene/Fehlerhafte Zeilen
                  </Typography>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell><strong>Zeile</strong></TableCell>
                          <TableCell><strong>Fehler</strong></TableCell>
                          <TableCell><strong>Daten</strong></TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {uploadResult.invalidRows.map((item, index) => (
                          <TableRow key={index} sx={{ '&:nth-of-type(odd)': { bgcolor: 'action.hover' } }}>
                            <TableCell>{item.rowNumber || index + 1}</TableCell>
                            <TableCell>
                              <Typography variant="body2" color="error">
                                {item.error}
                              </Typography>
                            </TableCell>
                            <TableCell>
                              <Typography variant="body2" sx={{ fontFamily: 'monospace', fontSize: '0.75rem' }}>
                                {JSON.stringify(item.row).substring(0, 100)}...
                              </Typography>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDetailsDialog(false)}>
            Schließen
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default CSVUpload;
